import { useState } from "react";
import { getFilePathWithBackendUrl } from "utils/helpers/common/http-methods";
import {
  Joinee,
  PostJoineeDTO,
} from "utils/helpers/models/host-dtos/postReviewJoinee.dto";
import { IoIosArrowDown } from "react-icons/io";
import { IoIosArrowUp } from "react-icons/io";
import { ReactComponent as CalandarIcon } from "assets/images/svgs/calendarDark.svg";
import { ReactComponent as ClockIcon } from "assets/images/svgs/clockdark.svg";
import attendTick from "assets/images/others/host-side-images/QR_tick.png";
import attendCross from "assets/images/others/host-side-images/QR_cross.png";
import useRsvp from "containers/rsvp/useHooks";

export default function AttendingParticipants(props: {
  participants: any[];
  eventId: number;
  workshopSessions: any[];
  eventAttendance: any[];
  attendanceDetails?: any[];
}) {
  const { participants, eventId, workshopSessions, eventAttendance } = props;
  const [open, setOpen] = useState(false);
  const [openRows, setOpenRows] = useState<number[]>([]);
  const { postReviewJoinee } = useRsvp();
  const workshopSessionsIds = workshopSessions.map((p) => p.id);
  const filteredAttendance = eventAttendance.filter((item) =>
    workshopSessionsIds.includes(item.workShopSessionId)
  );

  const latestAttendancePerSession = {};
  filteredAttendance.forEach((item) => {
    const sessionId = item.workShopSessionId;
    const existing = latestAttendancePerSession[sessionId];

    if (!existing || new Date(item.createdAt) < new Date(existing.createdAt)) {
      latestAttendancePerSession[sessionId] = item;
    }
  });

  const latestAttendances = Object.values(latestAttendancePerSession);
  const attendedSessionIds = filteredAttendance.map(
    (item) => item.workShopSessionId
  );

  const matchedWorkshopSessions = workshopSessions.filter((session) =>
    attendedSessionIds.includes(session.id)
  );

  const [respectfulChecked, setRespectfulChecked] = useState<PostJoineeDTO[]>(
    participants.map(
      (p) =>
        new PostJoineeDTO({
          joinees: [
            new Joinee({
              userId: p.id,
              wasRespectfull: false,
              wasOnTime: false,
            }),
          ],
        })
    )
  );

  const [onTimeChecked, setOnTimeChecked] = useState<PostJoineeDTO[]>(
    participants.map(
      (p) =>
        new PostJoineeDTO({
          joinees: [
            new Joinee({
              userId: p.id,
              wasRespectfull: false,
              wasOnTime: false,
            }),
          ],
        })
    )
  );

  const handleSelectAllRespectful = (checked: boolean) => {
    setRespectfulChecked(
      participants.map((p, i) => {
        const current = respectfulChecked[i];
        const existingJoinee =
          current?.joinees?.[0] ?? new Joinee({ userId: p.id });
        return new PostJoineeDTO({
          referralCode: current?.referralCode ?? "",
          joinees: [
            new Joinee({
              ...existingJoinee,
              wasRespectfull: checked,
            }),
          ],
        });
      })
    );
  };

  const handleSelectAllOnTime = (checked: boolean) => {
    setOnTimeChecked(
      participants.map((p, i) => {
        const current = onTimeChecked[i];
        const existingJoinee =
          current?.joinees?.[0] ?? new Joinee({ userId: p.id });
        return new PostJoineeDTO({
          referralCode: current?.referralCode ?? "",
          joinees: [
            new Joinee({
              ...existingJoinee,
              wasOnTime: checked,
            }),
          ],
        });
      })
    );
  };

  const handleRespectfulChange = (idx: number, checked: boolean) => {
    setRespectfulChecked((prev) =>
      prev.map((dto, i) =>
        i === idx
          ? new PostJoineeDTO({
              ...dto,
              joinees: [
                new Joinee({ ...dto.joinees[0], wasRespectfull: checked }),
              ],
            })
          : dto
      )
    );
  };

  const handleOnTimeChange = (idx: number, checked: boolean) => {
    setOnTimeChecked((prev) =>
      prev.map((dto, i) =>
        i === idx
          ? new PostJoineeDTO({
              ...dto,
              joinees: [new Joinee({ ...dto.joinees[0], wasOnTime: checked })],
            })
          : dto
      )
    );
  };

  const onSubmit = () => {
    const finalPayload = respectfulChecked.map((dto, idx) => {
      const onTime = onTimeChecked[idx]?.joinees[0]?.wasOnTime;
      return new PostJoineeDTO({
        referralCode: "123456",
        joinees: [
          new Joinee({
            ...dto.joinees[0],
            wasOnTime: onTime,
          }),
        ],
      });
    });
    postReviewJoinee(
      {
        joinees: finalPayload.map((f) => f.joinees[0]),
        referralCode: "123456",
      },
      eventId
    );
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  const toggleRow = (idx: number) => {
    setOpenRows((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  return (
    <div className="w-full pt-10 pb-5">
      <div className="flex items-center justify-end">
        <button
          className="bg-rsvpGreen rounded-md text-white px-4 py-2"
          onClick={onSubmit}
        >
          Submit
        </button>
      </div>
      {/* Header */}
      <div className="flex items-center border-b border-gray-200 pb-4 px-6">
        <div className="w-1/3 text-lg font-medium text-[#6B7280]">Name</div>
        <div className="w-1/3 flex items-center gap-2">
          <input
            type="checkbox"
            checked={
              respectfulChecked.every(
                (dto) => dto.joinees[0]?.wasRespectfull
              ) && respectfulChecked.length > 0
            }
            onChange={(e) => handleSelectAllRespectful(e.target.checked)}
            className="w-6 h-6 accent-primary rounded-lg border-[#DADADA] border-2"
          />
          <span className="text-lg font-semibold text-[#181945]">
            Select All
          </span>
        </div>
        <div className="w-1/3 flex items-center gap-2">
          <input
            type="checkbox"
            checked={
              onTimeChecked.every((dto) => dto.joinees[0]?.wasOnTime) &&
              onTimeChecked.length > 0
            }
            onChange={(e) => handleSelectAllOnTime(e.target.checked)}
            className="w-6 h-6 accent-primary rounded-lg border-[#DADADA] border-2"
          />
          <span className="text-lg font-semibold text-[#181945]">
            Select All
          </span>
        </div>
      </div>
      {/* Rows */}
      {participants.map((p, idx) => (
        <>
          <div
            key={p.id || idx}
            className="flex items-center  border-b-2 border-[#DDDDDD] py-6 px-6 pr-[10px]"
          >
            <div className="w-1/3 flex items-center gap-4">
              <img
                src={
                  getFilePathWithBackendUrl(p.profilePicUrl?.fileDownloadUri) ||
                  "https://picsum.photos/200/300"
                }
                alt={p.firstName || "Avatar"}
                className="w-12 h-12 rounded-full object-cover"
              />
              <span className="text-xl font-bold text-[#202224]">
                {p.firstName} {p.lastName}
              </span>
            </div>
            <div className="w-1/3 flex items-center gap-2">
              <input
                type="checkbox"
                checked={
                  respectfulChecked[idx]?.joinees[0]?.wasRespectfull || false
                }
                onChange={(e) => handleRespectfulChange(idx, e.target.checked)}
                className="w-6 h-6 accent-primary rounded-lg border-[#DADADA] border-2"
              />
              <span className="text-lg font-normal text-[#747688]">
                Respectfull
              </span>
            </div>
            <div className="w-1/3 flex items-center gap-2">
              <input
                type="checkbox"
                checked={onTimeChecked[idx]?.joinees[0]?.wasOnTime || false}
                onChange={(e) => handleOnTimeChange(idx, e.target.checked)}
                className="w-6 h-6 accent-primary rounded-lg border-[#DADADA] border-2"
              />
              <span className="text-lg font-normal text-[#747688]">
                On-Time
              </span>
            </div>
            <div
              className="flex items-center gap-2"
              onClick={() => toggleRow(idx)}
            >
              {openRows.includes(idx) ? (
                <IoIosArrowUp cursor={"pointer"} />
              ) : (
                <IoIosArrowDown cursor={"pointer"} />
              )}
            </div>
          </div>
          <div>
            {openRows.includes(idx) && (
              <div className="bg-[#F9FAFB] px-6 py-4">
                <h3 className="text-lg font-semibold mb-2">
                  Attended Sessions
                </h3>
                {/* {matchedWorkshopSessions.length > 0 ? (
                  matchedWorkshopSessions.map((session) => (
                    <div key={session.id} className='text-sm text-gray-700 mb-1'>
                         <div className="flex flex-wrap gap-4 text-base text-lightBlackText">
                      <p className="flex items-center gap-1">
                        <CalandarIcon /> {session?.startDateWithMonthAndYear}
                      </p>
                       <p className="flex items-center gap-1">
                      <ClockIcon /> {session?.startTime} to {session?.endTime}
                    </p>
                    <div >
                      <img src={attendTick} alt="attendTick"  className='w-[25px] h-[25px]'/>
                    </div>
                     </div> 
                    </div>
                  ))
                ) : (
                  <div className='text-sm text-gray-500'>No sessions attended</div>
                )} */}
                {workshopSessions.length > 0 ? (
                  workshopSessions.map((session) => {
                    const isAttended = attendedSessionIds.includes(session.id);
                    return (
                      <div
                        key={session.id}
                        className="text-sm text-gray-700 mb-1"
                      >
                        <div className="flex flex-wrap gap-4 text-base text-lightBlackText items-center">
                          <p className="flex items-center gap-1">
                            <CalandarIcon />{" "}
                            {session?.startDateWithMonthAndYear}
                          </p>
                          <p className="flex items-center gap-1">
                            <ClockIcon /> {session?.startTime} to{" "}
                            {session?.endTime}
                          </p>
                          <div>
                            <img
                              src={isAttended ? attendTick : attendCross}
                              alt={isAttended ? "attendTick" : "attendCross"}
                              className="w-[25px] h-[25px]"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-sm text-gray-500">
                    No sessions scheduled
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ))}
    </div>
  );
}
